import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Chip, type ChipVariant } from '../../components/ui/Chip';
import { CompactEmptyState } from '../../components/ui/EmptyState';
import { SmsReplyComposer } from './SmsReplyComposer';
import {
  useConversations,
  useConversation,
  useReply,
  useMarkRead,
  useSnooze,
  type ConversationListItem,
  type ConversationDetail,
  type ConversationMessage as ApiMessage,
  type ConversationFilters,
} from '../../api/inbox.api';

/* ── Screens 20, 06, 21 — Inbox (wired to live data in Sprint 7) ── */

type FolderId = 'all' | 'unread' | 'snoozed' | 'closed';
interface Folder { id: FolderId; label: string; count: number; icon: ReactNode }
interface CampaignFolder { id: string; name: string; count: number }
interface RecruiterFolder { id: string; name: string; initials: string; color: string; count: number }

/** Internal UI model — adapter target for ConversationListItem. */
interface Thread {
  id: string;
  initials: string;
  color: string;
  name: string;
  role: string;
  timeAgo: string;
  subject: string;
  preview: string;
  campaigns: string[];
  status?: ChipVariant;
  statusLabel?: string;
  unread?: boolean;
  channel?: 'EMAIL' | 'SMS';
}

interface ConversationMessage {
  id: string;
  kind: 'outbound' | 'inbound' | 'note';
  senderName: string;
  senderInitials: string;
  senderColor: string;
  timestamp: string;
  body: string;
}

type ComposerMode = 'reply' | 'note';

/* ── Live-data adapters ─────────────────────────────────────────────────── */

const AVATAR_COLORS = [
  'var(--color-brand-indigo)',
  'var(--color-brand-teal)',
  'var(--color-brand-blue)',
  'var(--color-warning)',
  'var(--color-brand-coral)',
];

function colorForId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initialsFor(first: string | null, last: string | null, email: string | null): string {
  const f = (first ?? '').trim();
  const l = (last ?? '').trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || '··';
  if (email) return email.charAt(0).toUpperCase();
  return '··';
}

function fullName(first: string | null, last: string | null, email: string | null): string {
  const f = (first ?? '').trim();
  const l = (last ?? '').trim();
  return [f, l].filter(Boolean).join(' ') || email || 'Unknown';
}

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const now = Date.now();
  const then = new Date(iso).getTime();
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yest';
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return new Date(iso).toLocaleDateString();
}

function messageTimestamp(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
}

function toThread(c: ConversationListItem): Thread {
  const contact = c.contact;
  const id = c.id;
  const name = fullName(contact?.first_name ?? null, contact?.last_name ?? null, contact?.email ?? null);
  const role = [contact?.title, contact?.company].filter(Boolean).join(' · ');
  const initials = initialsFor(contact?.first_name ?? null, contact?.last_name ?? null, contact?.email ?? null);

  let status: ChipVariant | undefined;
  let statusLabel: string | undefined;
  if (c.status === 'SNOOZED') { status = 'snoozed'; statusLabel = 'Snoozed'; }
  else if (c.status === 'CLOSED') { status = 'completed'; statusLabel = 'Closed'; }
  else if (c.unread) { status = 'awaiting'; statusLabel = 'Needs reply'; }
  else { status = 'open'; statusLabel = 'Open'; }

  // SMS conversations don't have subjects (Section 4 of the SMS addendum).
  // Use a channel-aware label instead so the row doesn't show "(no subject)"
  // for every text thread.
  const isSms = c.channel === 'SMS';
  const subject = isSms
    ? `SMS · ${c.contact_handle ?? contact?.email ?? 'unknown number'}`
    : (c.subject ?? '(no subject)');

  return {
    id,
    initials,
    color: colorForId(contact?.id ?? id),
    name,
    role: role || '-',
    timeAgo: timeAgo(c.last_message_at),
    subject,
    preview: c.preview ?? '',
    campaigns: [],
    status,
    statusLabel,
    unread: c.unread,
    channel: c.channel,
  };
}

function toUiMessage(m: ApiMessage, threadContact: ConversationDetail['contact'], orgUserName: string): ConversationMessage {
  const outbound = m.direction === 'OUTBOUND';
  const initials = outbound
    ? orgUserName.split(' ').map((p) => p.charAt(0)).join('').slice(0, 2).toUpperCase() || 'ME'
    : initialsFor(threadContact?.first_name ?? null, threadContact?.last_name ?? null, threadContact?.email ?? null);
  const senderName = outbound ? orgUserName : fullName(threadContact?.first_name ?? null, threadContact?.last_name ?? null, threadContact?.email ?? null);
  const senderColor = outbound ? 'var(--color-brand-indigo)' : colorForId(threadContact?.id ?? m.id);

  // SMS messages carry their payload in body_text (Section 4 of the SMS
  // addendum — subject + body_html are null). Wrap plain text in <pre> to
  // preserve line breaks without parsing it as HTML.
  const body = m.body_html
    ?? (m.body_text ? `<pre style="white-space:pre-wrap;font-family:inherit;margin:0;">${escapeHtml(m.body_text)}</pre>` : '');

  return {
    id: m.id,
    kind: outbound ? 'outbound' : 'inbound',
    senderName,
    senderInitials: initials,
    senderColor,
    timestamp: messageTimestamp(m.sent_at ?? m.created_at),
    body,
  };
}

/* ── Main page ──────────────────────────────────────────────────────────── */

export function InboxPage() {
  const [activeFolder, setActiveFolder] = useState<FolderId>('all');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [composerMode, setComposerMode] = useState<ComposerMode>('reply');
  const [search, setSearch] = useState('');

  const filters: ConversationFilters = useMemo(() => {
    if (activeFolder === 'unread') return { unread: true };
    if (activeFolder === 'snoozed') return { status: 'SNOOZED' };
    if (activeFolder === 'closed') return { status: 'CLOSED' };
    return {};
  }, [activeFolder]);

  const { data: conversationsData, isLoading: conversationsLoading } = useConversations(filters);
  const conversations = conversationsData?.data ?? [];

  // Default-select the first thread when none picked. When the active folder
  // changes we let the user explicitly pick something instead of auto-selecting
  // because filter switches usually mean they're hunting for something specific.
  useEffect(() => {
    if (!selectedThreadId && conversations.length > 0) {
      setSelectedThreadId(conversations[0].id);
    }
  }, [conversations, selectedThreadId]);

  const filteredThreads = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = conversations.map(toThread);
    if (!s) return list;
    return list.filter((t) => [t.name, t.subject, t.preview, t.role].join(' ').toLowerCase().includes(s));
  }, [conversations, search]);

  const unreadCount = conversations.filter((c) => c.unread).length;
  const populated = conversations.length > 0;

  // Derive folder counts from the (filtered) conversation pool. The "all"
  // folder shows total + we can't know "snoozed" count without a separate
  // query — left at 0 for now since the snoozed view loads its own list.
  const folders: Folder[] = [
    { id: 'all',     label: 'All replies', count: conversations.length, icon: <IconInbox size={14} /> },
    { id: 'unread',  label: 'Unread',      count: unreadCount,          icon: <IconClock size={14} /> },
    { id: 'snoozed', label: 'Snoozed',     count: 0,                    icon: <IconMoon size={14} /> },
    { id: 'closed',  label: 'Closed',      count: 0,                    icon: <IconCheck size={14} /> },
  ];

  // Derive campaign folder list from the conversation pool — counts are
  // approximate (only the conversations we've already fetched). Good enough
  // for a navigation aid; precise counts can wait for a dedicated endpoint.
  const campaigns: CampaignFolder[] = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; count: number }>();
    for (const c of conversations) {
      if (!c.campaign_id) continue;
      const existing = byId.get(c.campaign_id);
      if (existing) existing.count += 1;
      else byId.set(c.campaign_id, { id: c.campaign_id, name: c.campaign_id.slice(-6), count: 1 });
    }
    return Array.from(byId.values()).slice(0, 6);
  }, [conversations]);

  const selectedThread = filteredThreads.find((t) => t.id === selectedThreadId)
    ?? (filteredThreads.length > 0 ? filteredThreads[0] : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', minHeight: 0 }}>
      <Breadcrumb />
      <HeadingRow populated={populated} unread={unreadCount} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '220px 320px minmax(0, 1fr)',
          gap: 'var(--space-3)',
          alignItems: 'stretch',
          minHeight: '720px',
        }}
      >
        <FolderPanel
          folders={folders}
          campaigns={campaigns}
          recruiters={[]}
          active={activeFolder}
          onSelect={(f) => setActiveFolder(f as FolderId)}
        />

        <ThreadList
          threads={filteredThreads}
          selectedId={selectedThread?.id ?? null}
          onSelect={setSelectedThreadId}
          search={search}
          onSearch={setSearch}
          isNoteFolder={false}
          empty={!populated && !conversationsLoading}
        />

        <ConversationPanel
          threadId={selectedThread?.id ?? null}
          composerMode={composerMode}
          onChangeMode={setComposerMode}
        />
      </div>
    </div>
  );
}

/* ── Breadcrumb / heading ── */

function Breadcrumb() {
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
        <li><a href="/dashboard" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>Engage</a></li>
        <li aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}><IconChevronRight size={12} /></li>
        <li aria-current="page" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>Inbox</li>
      </ol>
    </nav>
  );
}

function HeadingRow({ populated, unread }: { populated: boolean; unread: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)', lineHeight: 'var(--leading-tight)' }}>
          Inbox · all replies in one place
        </h1>
        <p style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-muted)' }}>
          {populated ? `${unread} unread` : 'No active conversations yet.'}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        {/* Filter UX is folder-based on the left panel (Unread, Snoozed,
            campaigns, recruiters). A dedicated header Filter button would
            duplicate that surface, so we omit it until there's a use case
            the folder picker can't satisfy. */}
      </div>
    </div>
  );
}

/* ── Folder panel ── */

function FolderPanel({
  folders, campaigns, recruiters: _recruiters, active, onSelect,
}: {
  folders: Folder[];
  campaigns: CampaignFolder[];
  recruiters: RecruiterFolder[];
  active: string;
  onSelect: (id: string) => void;
}) {
  return (
    <aside
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <FolderSection title="Folders">
          {folders.map((f) => (
            <FolderRow key={f.id} icon={f.icon} label={f.label} count={f.count} active={active === f.id} onSelect={() => onSelect(f.id)} />
          ))}
        </FolderSection>

        <FolderSection title="By campaign">
          {campaigns.length > 0 ? (
            campaigns.map((c) => (
              <FolderRow key={c.id} icon={<IconHash size={14} />} label={c.name} count={c.count} active={active === `c-${c.id}`} onSelect={() => onSelect(`c-${c.id}`)} />
            ))
          ) : (
            <FolderEmptyHint>Campaigns will appear here once they have replies.</FolderEmptyHint>
          )}
        </FolderSection>
      </div>
    </aside>
  );
}

function FolderSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div style={{ padding: 'var(--space-3) var(--space-4) var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
        {title}
      </div>
      <div style={{ padding: '0 var(--space-2) var(--space-2)' }}>{children}</div>
    </div>
  );
}

function FolderRow({ icon, label, count, active, onSelect }: { icon: ReactNode; label: string; count: number; active: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        backgroundColor: active ? 'var(--color-sidebar-active-bg)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-input)',
        color: active ? 'var(--color-sidebar-active-text)' : 'var(--color-text-secondary)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: active ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background-color var(--transition-base)',
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'var(--color-surface-overlay)'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <span style={{ color: active ? 'var(--color-sidebar-active-text)' : 'var(--color-text-muted)', display: 'inline-flex' }}>{icon}</span>
      <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{count}</span>
    </button>
  );
}

function FolderEmptyHint({ children }: { children: ReactNode }) {
  return (
    <p style={{ padding: 'var(--space-2) var(--space-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-subtle)', fontStyle: 'italic', lineHeight: 'var(--leading-snug)' }}>
      {children}
    </p>
  );
}

/* ── Thread list ── */

function ThreadList({
  threads, selectedId, onSelect, search, onSearch, empty,
}: {
  threads: Thread[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  search: string;
  onSearch: (v: string) => void;
  // Reserved for the post-MVP "Has internal note" filter — currently unused.
  isNoteFolder?: boolean;
  empty: boolean;
}) {
  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: 'var(--space-3)', borderBottom: '1px solid var(--color-border-default)' }}>
        <SearchInput value={search} onChange={onSearch} />
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {empty || threads.length === 0 ? (
          <div style={{ padding: 'var(--space-5)' }}>
            <CompactEmptyState
              icon={<IconInbox size={16} />}
              message={empty ? 'Campaign responses will appear here as they come in.' : 'No threads match this folder yet.'}
              action={empty ? <a href="/campaigns" style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-link)', textDecoration: 'none', whiteSpace: 'nowrap' }}>Go to campaigns →</a> : undefined}
            />
            {empty && (
              <h3 style={{ marginTop: 'var(--space-4)', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
                No replies yet
              </h3>
            )}
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {threads.map((t, i) => (
              <ThreadRow key={t.id} thread={t} selected={t.id === selectedId} onSelect={() => onSelect(t.id)} firstRow={i === 0} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function ThreadRow({ thread, selected, onSelect, firstRow }: { thread: Thread; selected: boolean; onSelect: () => void; firstRow: boolean }) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: 'var(--space-3) var(--space-4)',
          borderTop: firstRow ? 'none' : '1px solid var(--color-border-default)',
          borderLeft: `3px solid ${selected ? 'var(--color-action-primary)' : 'transparent'}`,
          backgroundColor: selected ? 'var(--color-sidebar-active-bg)' : 'transparent',
          border: 'none',
          display: 'flex',
          gap: 'var(--space-3)',
          cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
        }}
        onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--color-surface-overlay)'; }}
        onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <Avatar initials={thread.initials} color={thread.color} size="md" />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', minWidth: 0 }}>
              {thread.unread && <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-action-primary)', flexShrink: 0 }} />}
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: thread.unread ? 'var(--font-weight-bold)' : 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{thread.name}</span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', flexShrink: 0 }}>
              {thread.channel === 'SMS' && (
                <span aria-label="SMS conversation" title="SMS conversation" style={{ fontSize: '10px', fontFamily: 'var(--font-mono)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-action-primary-text)', backgroundColor: 'var(--color-action-primary)', padding: '2px 6px', borderRadius: 'var(--radius-full)', letterSpacing: 'var(--tracking-caps)' }}>
                  SMS
                </span>
              )}
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{thread.timeAgo}</span>
            </span>
          </div>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{thread.role}</span>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{thread.subject}</span>

          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-snug)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{thread.preview}</span>

          <div style={{ marginTop: 'var(--space-1)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
            {thread.campaigns.map((c) => <CampaignTag key={c}>{c}</CampaignTag>)}
            {thread.status && thread.statusLabel && <Chip variant={thread.status}>{thread.statusLabel}</Chip>}
          </div>
        </div>
      </button>
    </li>
  );
}

function CampaignTag({ children }: { children: ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: 'var(--chip-padding-y) var(--chip-padding-x)', borderRadius: 'var(--chip-radius)', backgroundColor: 'var(--color-navy-50)', border: '1px solid var(--color-navy-100)', color: 'var(--color-text-brand)', fontFamily: 'var(--font-sans)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)', letterSpacing: 'var(--tracking-wide)' }}>
      #{children}
    </span>
  );
}

/* ── Conversation panel ── */

function ConversationPanel({
  threadId, composerMode, onChangeMode,
}: {
  threadId: string | null;
  composerMode: ComposerMode;
  onChangeMode: (m: ComposerMode) => void;
}) {
  const { data: conv, isLoading } = useConversation(threadId);
  const markRead = useMarkRead();
  const snooze = useSnooze();
  const reply = useReply(threadId);

  // Auto mark-read when a thread is opened — the spec calls for the "unread"
  // badge to clear as soon as a recruiter views it. Guarded by `unread` so
  // we don't bombard the API on every re-render of the same selected thread.
  useEffect(() => {
    if (conv?.id && conv.unread) {
      markRead.mutate(conv.id);
    }
    // markRead is intentionally not in deps — it's a stable mutation hook
    // and including it would cause the effect to re-fire on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv?.id, conv?.unread]);

  if (!threadId) {
    return (
      <section style={panelStyle}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CompactEmptyState
            icon={<IconMail size={16} />}
            message="Select a conversation to view it."
          />
        </div>
      </section>
    );
  }

  if (isLoading || !conv) {
    return (
      <section style={panelStyle}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Loading…</span>
        </div>
      </section>
    );
  }

  const contactName = fullName(conv.contact?.first_name ?? null, conv.contact?.last_name ?? null, conv.contact?.email ?? null);
  const contactRole = [conv.contact?.title, conv.contact?.company].filter(Boolean).join(' · ');

  return (
    <section style={panelStyle}>
      <ConversationHeader
        contactName={contactName}
        contactRole={contactRole || '-'}
        campaign={conv.campaign}
        status={conv.status}
        snoozed={!!conv.snoozed_until}
        onSnoozeOneDay={() => snooze.mutate({
          conversationId: conv.id,
          until: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        })}
        onUnsnooze={() => snooze.mutate({ conversationId: conv.id, until: null })}
      />
      <ConversationThread detail={conv} />
      {conv.channel === 'SMS' ? (
        <SmsReplyComposer
          optedOut={!!conv.sms_opted_out}
          sending={reply.isPending}
          onSend={async (body) => {
            await reply.mutateAsync({ body_text: body });
          }}
        />
      ) : (
        <Composer
          mode={composerMode}
          onChangeMode={onChangeMode}
          defaultSubject={defaultReplySubject(conv)}
          sending={reply.isPending}
          canReply={!!conv.contact?.email}
          onSend={async ({ subject, body }) => {
            await reply.mutateAsync({
              subject,
              body_html: body,
              body_text: body.replace(/<[^>]+>/g, '').trim(),
            });
          }}
        />
      )}
    </section>
  );
}

const panelStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: 'var(--card-radius)',
  boxShadow: 'var(--card-shadow)',
  display: 'flex' as const,
  flexDirection: 'column' as const,
  overflow: 'hidden' as const,
  minWidth: 0,
};

function defaultReplySubject(conv: ConversationDetail): string {
  // Find the most recent message and prefix Re: if it doesn't already have one.
  const last = [...conv.messages].reverse()[0];
  const subj = last?.subject ?? '';
  if (!subj) return '';
  return /^re:\s/i.test(subj) ? subj : `Re: ${subj}`;
}

function ConversationHeader({
  contactName, contactRole, campaign, status, snoozed, onSnoozeOneDay, onUnsnooze,
}: {
  contactName: string;
  contactRole: string;
  campaign: { id: string; name: string; type: string } | null;
  status: ConversationDetail['status'];
  snoozed: boolean;
  onSnoozeOneDay: () => void;
  onUnsnooze: () => void;
}) {
  let chip: { variant: ChipVariant; label: string } | null = null;
  if (status === 'SNOOZED') chip = { variant: 'snoozed', label: 'Snoozed' };
  else if (status === 'CLOSED') chip = { variant: 'completed', label: 'Closed' };
  else chip = { variant: 'open', label: 'Open' };

  return (
    <header
      style={{
        padding: 'var(--space-4) var(--space-5)',
        borderBottom: '1px solid var(--color-border-default)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
            {contactName}
          </h2>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{contactRole}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0 }}>
          <Chip variant={chip.variant}>{chip.label}</Chip>
          {snoozed
            ? <SecondaryButton onClick={onUnsnooze}><IconMoon size={14} /><span style={{ marginLeft: 'var(--space-1)' }}>Unsnooze</span></SecondaryButton>
            : <SecondaryButton onClick={onSnoozeOneDay}><IconMoon size={14} /><span style={{ marginLeft: 'var(--space-1)' }}>Snooze 24h</span></SecondaryButton>
          }
          <IconButton aria-label="More options"><IconMore size={14} /></IconButton>
        </div>
      </div>

      {campaign && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            <IconHash size={11} /> {campaign.name}
          </span>
        </div>
      )}
    </header>
  );
}

function ConversationThread({ detail }: { detail: ConversationDetail }) {
  // "Me" name for outbound bubbles. The conversation contact is the *other*
  // party — we don't track per-message sender_user_id yet, so we display a
  // generic recruiter label. When we wire user lookup we can replace this.
  const orgUserName = 'You';
  const messages = detail.messages.map((m) => toUiMessage(m, detail.contact, orgUserName));

  if (messages.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-5)' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          No messages in this thread yet.
        </span>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', backgroundColor: 'var(--color-surface-page)' }}>
      {messages.map((m) => <MessageBubble key={m.id} message={m} />)}
    </div>
  );
}

function MessageBubble({ message }: { message: ConversationMessage }) {
  if (message.kind === 'note') return <NoteBubble message={message} />;
  const outbound = message.kind === 'outbound';
  return (
    <div style={{ display: 'flex', flexDirection: outbound ? 'row-reverse' : 'row', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
      <Avatar initials={message.senderInitials} color={message.senderColor} size="md" />
      <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', alignItems: outbound ? 'flex-end' : 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{message.senderName}</span>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{message.timestamp}</span>
        </div>
        <div
          style={{
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-card)',
            backgroundColor: outbound ? 'var(--color-action-primary)' : 'var(--color-surface-white)',
            color: outbound ? 'var(--color-action-primary-text)' : 'var(--color-text-primary)',
            border: outbound ? 'none' : '1px solid var(--color-border-default)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            lineHeight: 'var(--leading-relaxed)',
            whiteSpace: 'pre-wrap',
          }}
          dangerouslySetInnerHTML={{ __html: message.body }}
        />
      </div>
    </div>
  );
}

function NoteBubble({ message }: { message: ConversationMessage }) {
  return (
    <div
      style={{
        marginLeft: 'var(--space-12)',
        borderLeft: '4px solid var(--color-note-accent)',
        backgroundColor: 'var(--color-note-bg)',
        border: '1px solid var(--color-note-border)',
        borderLeftWidth: '4px',
        borderLeftColor: 'var(--color-note-accent)',
        borderRadius: '0 var(--radius-card) var(--radius-card) 0',
        padding: 'var(--space-3) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-note-text)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
          Internal note
        </span>
        <span aria-hidden="true" style={{ color: 'var(--color-note-text)' }}>·</span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
          {message.senderName}
        </span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-note-text)' }}>
          {message.timestamp}
        </span>
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-note-text)', fontStyle: 'italic' }}>
          Only your team can see this
        </span>
      </div>
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-note-text)', lineHeight: 'var(--leading-relaxed)' }}>
        {message.body}
      </p>
    </div>
  );
}

/* ── Composer ── */

interface ComposerProps {
  mode: ComposerMode;
  onChangeMode: (m: ComposerMode) => void;
  defaultSubject: string;
  sending: boolean;
  canReply: boolean;
  onSend: (args: { subject: string; body: string }) => Promise<void>;
}

function Composer({ mode, onChangeMode, defaultSubject, sending, canReply, onSend }: ComposerProps) {
  return (
    <div style={{ borderTop: '1px solid var(--color-border-default)', backgroundColor: 'var(--color-surface-white)' }}>
      <ComposerTabs mode={mode} onChangeMode={onChangeMode} />
      {mode === 'reply'
        ? <ReplyComposer defaultSubject={defaultSubject} sending={sending} canReply={canReply} onSend={onSend} />
        : <NoteComposer />}
    </div>
  );
}

function ComposerTabs({ mode, onChangeMode }: { mode: ComposerMode; onChangeMode: (m: ComposerMode) => void }) {
  const tabs: { id: ComposerMode; label: string }[] = [
    { id: 'reply', label: 'Reply to candidate' },
    { id: 'note',  label: 'Internal note' },
  ];
  return (
    <div role="tablist" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: '0 var(--space-4)', borderBottom: '1px solid var(--color-border-default)' }}>
      {tabs.map((t) => {
        const isActive = t.id === mode;
        const isNote = t.id === 'note';
        const accent = isNote ? 'var(--color-note-accent)' : 'var(--color-action-primary)';
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChangeMode(t.id)}
            style={{
              height: 'var(--btn-height-lg)',
              padding: '0 var(--space-3)',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${isActive ? accent : 'transparent'}`,
              marginBottom: '-1px',
              color: isActive ? (isNote ? 'var(--color-note-text)' : 'var(--color-text-brand)') : 'var(--color-text-muted)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
            }}
          >
            {isNote && <IconNote size={11} />}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function ReplyComposer({
  defaultSubject, sending, canReply, onSend,
}: {
  defaultSubject: string;
  sending: boolean;
  canReply: boolean;
  onSend: (args: { subject: string; body: string }) => Promise<void>;
}) {
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Re-seed the subject when a different thread loads — without this the
  // subject from the previous thread sticks around.
  useEffect(() => { setSubject(defaultSubject); setBody(''); setError(null); }, [defaultSubject]);

  async function submit() {
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required.');
      return;
    }
    setError(null);
    try {
      // Send body as plain text wrapped in <p>… so the API stores it as
      // body_html. The server-side text fallback strips tags for the plain
      // mime part. Rich-text editor is a post-MVP enhancement.
      const html = body
        .split(/\n\n+/)
        .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br/>')}</p>`)
        .join('\n');
      await onSend({ subject, body: html });
      setBody('');
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        ?? (err as Error).message
        ?? 'Failed to send reply';
      setError(message);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-border-default)', flexWrap: 'wrap' }}>
        <ComposerAction tone="linki" disabled><IconSpark size={12} /><span style={{ marginLeft: 'var(--space-1)' }}>Suggest reply (soon)</span></ComposerAction>
      </div>

      <div style={{ padding: 'var(--space-3) var(--space-4)', backgroundColor: 'var(--color-warning-bg)', borderBottom: '1px solid var(--color-warning-border)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <IconAlert size={12} />
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-warning)', flex: 1 }}>
          Sending will stop any active campaign step for this candidate.
        </span>
      </div>

      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
        style={{
          width: '100%',
          padding: 'var(--space-2) var(--space-4)',
          border: 'none',
          borderBottom: '1px solid var(--color-border-default)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          outline: 'none',
          backgroundColor: 'transparent',
        }}
      />

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Type your reply…"
        rows={5}
        style={{
          width: '100%',
          padding: 'var(--space-3) var(--space-4)',
          border: 'none',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-md)',
          color: 'var(--color-text-primary)',
          resize: 'vertical',
          outline: 'none',
          backgroundColor: 'transparent',
        }}
      />

      {error && (
        <div role="alert" style={{ padding: 'var(--space-2) var(--space-4)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--color-border-default)', backgroundColor: 'var(--color-surface-muted)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
          {!canReply && (
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              No email on file for this contact.
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={sending || !canReply}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 'var(--btn-height)',
            padding: '0 var(--btn-padding-x)',
            borderRadius: 'var(--btn-radius)',
            backgroundColor: 'var(--color-action-primary)',
            color: 'var(--color-action-primary-text)',
            border: 'none',
            fontSize: 'var(--btn-font-size)',
            fontWeight: 'var(--btn-font-weight)',
            fontFamily: 'var(--font-sans)',
            cursor: sending || !canReply ? 'not-allowed' : 'pointer',
            opacity: sending || !canReply ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          <IconSend size={14} /><span style={{ marginLeft: 'var(--space-1)' }}>{sending ? 'Sending…' : 'Send'}</span>
        </button>
      </div>
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function NoteComposer() {
  // Internal notes (mentions / tags / summarise / post) aren't on the
  // backend yet — there's no schema, no endpoint. Rather than ship a fake
  // composer that drops every keystroke, surface this as an honest
  // not-built-yet panel so users don't try to take notes and lose them.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: 'var(--space-6)', backgroundColor: 'var(--color-note-bg)', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--chip-padding-y) var(--chip-padding-x)', borderRadius: 'var(--chip-radius)', backgroundColor: 'var(--chip-hasnote-bg)', border: '1px solid var(--chip-hasnote-border)', color: 'var(--chip-hasnote-text)', fontFamily: 'var(--font-sans)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)', letterSpacing: 'var(--tracking-wide)' }}>
        <IconNote size={10} /> Internal notes: coming soon
      </span>
      <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
        Internal notes (mentions, tags, AI thread summaries) aren't built yet. They'll live in this composer when the backend lands.
        For now, jot context in JT's candidate notes from the contact's profile.
      </p>
    </div>
  );
}

function ComposerAction({ children, tone = 'default', disabled = false }: { children: ReactNode; tone?: 'default' | 'linki' | 'note'; disabled?: boolean }) {
  const styles: Record<string, { bg: string; fg: string; border: string }> = {
    default: { bg: 'var(--color-action-secondary)', fg: 'var(--color-action-secondary-text)', border: 'var(--color-action-secondary-border)' },
    linki:   { bg: 'var(--color-brand-teal)',       fg: 'var(--color-text-inverse)',          border: 'var(--color-brand-teal)' },
    note:    { bg: 'var(--color-surface-white)',    fg: 'var(--color-note-text)',             border: 'var(--color-note-border)' },
  };
  const s = styles[tone];
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

/* ── Shared bits ── */

function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ position: 'relative' }}>
      <span aria-hidden="true" style={{ position: 'absolute', left: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-subtle)', pointerEvents: 'none' }}>
        <IconSearch size={14} />
      </span>
      <input
        type="search"
        placeholder="Search threads…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', height: 'var(--input-height-sm)', paddingLeft: 'var(--space-8)', paddingRight: 'var(--space-8)', border: '1px solid var(--input-border)', borderRadius: 'var(--input-radius)', backgroundColor: 'var(--color-surface-muted)', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-sans)', color: 'var(--color-text-primary)', outline: 'none' }}
        onFocus={(e) => { e.target.style.borderColor = 'var(--input-border-focus)'; e.target.style.boxShadow = 'var(--shadow-focus)'; e.target.style.backgroundColor = 'var(--color-surface-white)'; }}
        onBlur={(e) => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.boxShadow = 'none'; e.target.style.backgroundColor = 'var(--color-surface-muted)'; }}
      />
      <span aria-hidden="true" style={{ position: 'absolute', right: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-subtle)' }}>
        <IconFilter size={14} />
      </span>
    </div>
  );
}

function Avatar({ initials, color, size = 'md' }: { initials: string; color: string; size?: 'sm' | 'md' | 'lg' }) {
  const dim = size === 'lg' ? 'var(--avatar-size-lg)' : size === 'md' ? 'var(--avatar-size-md)' : 'var(--avatar-size-sm)';
  const font = size === 'sm' ? 'var(--avatar-font-size-sm)' : 'var(--avatar-font-size-md)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: dim, height: dim, borderRadius: 'var(--avatar-radius)', backgroundColor: color, color: 'var(--color-text-inverse)', fontFamily: 'var(--font-sans)', fontSize: font, fontWeight: 'var(--font-weight-semibold)', flexShrink: 0 }}>
      {initials}
    </span>
  );
}

/* ── Buttons ── */

function SecondaryButton({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return <button type="button" onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--btn-height)', padding: '0 var(--btn-padding-x)', borderRadius: 'var(--btn-radius)', backgroundColor: 'var(--color-action-secondary)', color: 'var(--color-action-secondary-text)', border: '1px solid var(--color-action-secondary-border)', fontSize: 'var(--btn-font-size)', fontWeight: 'var(--btn-font-weight)', fontFamily: 'var(--font-sans)', cursor: 'pointer', whiteSpace: 'nowrap' }}>{children}</button>;
}

function IconButton({ children, 'aria-label': ariaLabel }: { children: ReactNode; 'aria-label': string }) {
  return <button type="button" aria-label={ariaLabel} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'var(--btn-height)', height: 'var(--btn-height)', borderRadius: 'var(--btn-radius)', backgroundColor: 'var(--color-action-secondary)', border: '1px solid var(--color-action-secondary-border)', color: 'var(--color-text-muted)', cursor: 'pointer' }}>{children}</button>;
}

/* ── Icons ── */

function IconChevronRight({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>; }
function IconInbox({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>; }
function IconClock({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>; }
function IconSend({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>; }
function IconMoon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>; }
function IconNote({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" /></svg>; }
function IconHash({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></svg>; }
function IconFilter({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>; }
function IconCheck({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>; }
function IconSearch({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function IconMore({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></svg>; }
function IconMail({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" /></svg>; }
function IconAlert({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>; }
function IconSpark({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.39 6.96L21 11l-6.61 2.04L12 20l-2.39-6.96L3 11l6.61-2.04L12 2z" /></svg>; }
